import { withAuth, AuthPageProps } from '../../context/Auth';
import { BlueTextM, GrayTextM, Headline } from '../../ui/Styled/Styled';

import {
    AssignedCourse,
    RecipiencyDocumentsData
} from 'api/courses/courses.types';
import {
    changeAssignmentRecipiencyDocumentsType,
    getCourseAssignment
} from 'api/courses/courses';
import { FC, useEffect, useMemo, useState, useCallback } from 'react';
import Skeleton from 'components/Skeleton/Skeleton';
import { getStudentFullname } from 'utils/fullname';
import * as S from './Documents.styled';
import { S as TableS } from 'ui/Table';
import { formatDate } from 'utils/date';
import Button from 'ui/Button/Button';
import { RouterPaths } from 'helpers/router-paths';
import { useNumberId } from 'hooks/useId';
import { DOCUMENTS_PROPS, recipiencyDocumentsTypeOptions } from './constants';
import { Container } from 'components/Container';
import { DocumentType, RecipiencyDocumentsType } from 'api/api';
import ButtonsContainer from 'ui/Button/ButtonContainer';
import * as SInput from 'ui/Input/Input.styled';
import { PRODUCTION_URL } from 'helpers/constants';
import { copyToClipboard } from 'utils/copy';
import { BlueLink } from 'ui/Link';
import { payAssignFromBalance } from 'api/money/money';
import { useCurrentOrganizationRequired } from 'context/User';
import { useIsAdmin } from 'context/User/hooks/roles';
import { toast } from 'react-toastify';
import { formatMoney } from 'utils/intl';
import { usePopup, usePopupClose } from 'components/Popup';
import { downloadAssignsReport } from 'api/reports/reports';
import { OrganizationFullInfo } from 'api/organizations/organizations.types';
import { DocumentRow } from './components/DocumentRow';
import { EditForm } from 'ui/EditForm';
import { SelectField } from 'ui/Select';
import { AddressField } from 'components/fields';
import FormField from 'ui/FormField/FormField';
type PaymentConfirmationPopupProps = {
    assignId: number;
    coursePrice: number;
    organizationBalance: number;
    organizationId: number;
    onPaySuccess: () => void;
};

const PaymentConfirmationPopup: FC<PaymentConfirmationPopupProps> = ({
    assignId,
    coursePrice,
    organizationBalance,
    organizationId,
    onPaySuccess
}) => {
    const close = usePopupClose();

    const handlePayFromBalance = useCallback(async () => {
        await payAssignFromBalance(organizationId, assignId);
        toast.success('Оплата прошла успешно');
        onPaySuccess();
        close();
    }, [organizationId, assignId, onPaySuccess, close]);

    return (
        <>
            <Headline>Подтверждение оплаты</Headline>
            <GrayTextM>
                Вы действительно хотите оплатить с баланса организации?
            </GrayTextM>
            <S.DatesRow>
                <GrayTextM>
                    Сумма к списанию:{' '}
                    <BlueTextM as="span">{formatMoney(coursePrice)}</BlueTextM>
                </GrayTextM>
            </S.DatesRow>
            <S.DatesRow>
                <GrayTextM>
                    Текущий баланс:{' '}
                    <BlueTextM as="span">
                        {formatMoney(organizationBalance)}
                    </BlueTextM>
                </GrayTextM>
            </S.DatesRow>
            <ButtonsContainer>
                <Button onClick={handlePayFromBalance}>Оплатить</Button>
            </ButtonsContainer>
        </>
    );
};

type AssignmentPageContentProps = {
    assignment: AssignedCourse;
    organization: OrganizationFullInfo;
    documentsLockStatuses: Record<DocumentType, boolean>;
    id: number;
    payLink: string;
    copyHandler: () => void;
    onPaySuccess: () => void;
};

const Field = FormField<RecipiencyDocumentsData>;

const AssignmentPageContent: FC<AssignmentPageContentProps> = ({
    assignment,
    organization,
    documentsLockStatuses,
    id,
    payLink,
    copyHandler,
    onPaySuccess
}) => {
    const isAdmin = useIsAdmin();

    const openPaymentPopup = usePopup(() => (
        <PaymentConfirmationPopup
            assignId={id}
            coursePrice={assignment?.course?.price ?? 0}
            organizationBalance={organization.balance?.amount ?? 0}
            organizationId={organization.id}
            onPaySuccess={onPaySuccess}
        />
    ));

    const changeRecipiencySubmitHandler = useCallback(
        (values: RecipiencyDocumentsData) => {
            return changeAssignmentRecipiencyDocumentsType(id, values);
        },
        [id]
    );

    return (
        <S.Container>
            <Headline $disableMargin>
                {getStudentFullname(assignment.student)}
            </Headline>
            <S.AssigmentPaidStatus $paid={assignment.isPaid}>
                {assignment.isPaid ? 'Оплачено' : 'Не оплачено'}
            </S.AssigmentPaidStatus>
            <S.DatesRow>
                <GrayTextM>
                    Дата начала обучения:{' '}
                    <BlueTextM as="span">
                        {formatDate(assignment.dateStartLearning)}
                    </BlueTextM>
                </GrayTextM>
                <GrayTextM>
                    Дата окончания обучения:{' '}
                    <BlueTextM as="span">
                        {formatDate(assignment.dateEndLearning)}
                    </BlueTextM>
                </GrayTextM>
            </S.DatesRow>
            <S.DatesRow>
                <GrayTextM>
                    Баланс организации:{' '}
                    <BlueTextM as="span">
                        {formatMoney(organization.balance?.amount ?? 0)}
                    </BlueTextM>
                </GrayTextM>
            </S.DatesRow>
            <TableS.Container>
                <TableS.Body>
                    {!!assignment.id &&
                        assignment.course?.issuedDocuments?.map((doc) => (
                            <DocumentRow
                                data={assignment.documents?.[doc] ?? []}
                                document={doc}
                                key={doc}
                                locked={documentsLockStatuses?.[doc]}
                                isPaid={assignment.isPaid ?? false}
                                openPaymentPopup={openPaymentPopup}
                                assignmentId={id}
                                isAdmin={isAdmin}
                            />
                        ))}
                </TableS.Body>
            </TableS.Container>

            <S.AssignPayContainer>
                {!assignment.isPaid && (
                    <S.AssignPayInner>
                        <SInput.Container as="div">
                            <SInput.Label>
                                Ссылка на оплату назначения
                            </SInput.Label>
                            <S.CopyInputContainer>
                                <S.CopyInputLink target="_blank" to={payLink}>
                                    {payLink}
                                </S.CopyInputLink>

                                <S.CopyInputIcon
                                    icon="copy"
                                    size="s"
                                    onClick={copyHandler}
                                />
                            </S.CopyInputContainer>
                        </SInput.Container>
                    </S.AssignPayInner>
                )}
                <EditForm
                    saver={changeRecipiencySubmitHandler}
                    successMessage="Способ получения документов изменен"
                    initialValues={assignment.recipiencyDocuments}
                >
                    {({ values, dirty }) => (
                        <>
                            <SelectField<RecipiencyDocumentsData>
                                name="type"
                                options={recipiencyDocumentsTypeOptions}
                                label="Способ получения документов"
                            />
                            {values.type === RecipiencyDocumentsType.Post && (
                                <>
                                    <AddressField<RecipiencyDocumentsData> name="address" />
                                    <Field name="surname" label="Фамилия" />
                                    <Field name="name" label="Имя" />
                                    <Field name="lastname" label="Отчество" />
                                </>
                            )}
                            {dirty && (
                                <div>
                                    <S.RecipiencyDocumentsButton type="submit">
                                        Сохранить
                                    </S.RecipiencyDocumentsButton>
                                </div>
                            )}
                        </>
                    )}
                </EditForm>
            </S.AssignPayContainer>
        </S.Container>
    );
};

const Assignment: FC<AuthPageProps> = () => {
    const id = useNumberId();
    const [assignment, setAssignment] = useState<AssignedCourse>();
    const organization = useCurrentOrganizationRequired();

    const onPaySuccess = useCallback(() => {
        if (!assignment) {
            return;
        }

        setAssignment({
            ...assignment,
            isPaid: true
        });
    }, [assignment]);

    useEffect(() => {
        getCourseAssignment(id).then(setAssignment);
    }, [id]);

    const documentsLockStatuses = useMemo<Record<DocumentType, boolean>>(
        () =>
            assignment?.documents
                ? Object.keys(assignment.documents).reduce(
                      (acc, doc) => ({
                          ...acc,
                          [doc]: assignment.documents![
                              doc as DocumentType
                          ]?.every(({ file }) => !!file)
                      }),
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      {} as any
                  )
                : {},
        [assignment?.documents]
    );

    const payLink = useMemo(
        () => `${PRODUCTION_URL}${RouterPaths.PayAssign(id)}`,
        [id]
    );

    const copyHandler = useCallback(
        () => payLink && copyToClipboard(String(payLink), 'URL скопирован'),
        [payLink]
    );

    const downloadAllHandler = useCallback(() => {
        return downloadAssignsReport([id]);
    }, [id]);

    if (!assignment) {
        return (
            <Container {...DOCUMENTS_PROPS}>
                <Skeleton />
            </Container>
        );
    }

    return (
        <S.Wrapper>
            <Container
                {...DOCUMENTS_PROPS}
                flex
                heading={
                    <BlueLink
                        to={RouterPaths.SingleCourse(assignment.course?.id)}
                    >
                        {assignment.course?.title}{' '}
                    </BlueLink>
                }
                headingRightContent={
                    assignment.documents &&
                    Object.keys(assignment.documents).length > 0 && (
                        <Button icon="download" onClick={downloadAllHandler}>
                            Скачать все
                        </Button>
                    )
                }
            >
                <AssignmentPageContent
                    assignment={assignment}
                    organization={organization}
                    documentsLockStatuses={documentsLockStatuses}
                    id={id}
                    payLink={payLink}
                    copyHandler={copyHandler}
                    onPaySuccess={onPaySuccess}
                />
            </Container>
        </S.Wrapper>
    );
};

export default withAuth(Assignment);
